import { Move, Trash2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface SignatureField {
  id: string;
  type: 'signature' | 'date';
  signatoryIndex: number; // 0 = employer, 1 = employee
  pageNumber: number; // 1-indexed
  x: number; // PDF points
  y: number; // PDF points
  width: number; // PDF points
  height: number; // PDF points
  label?: string;
}

interface Signatory {
  name: string;
  email: string;
  role: string;
  color: string;
}

interface SignatureFieldOverlayProps {
  fields: SignatureField[];
  signatories: Signatory[];
  currentPage: number;
  scale: number;
  pageRef: React.RefObject<HTMLDivElement | null> | null;
  /** Direct element reference (alternative to pageRef) */
  pageElement?: HTMLDivElement | null;
  /** Base PDF width in points (default 612 for standard letter size) */
  pageWidth?: number;
  /** Actual PDF page dimensions in points. Default to US-Letter so existing callers are unchanged. */
  pageWidthPt?: number;
  pageHeightPt?: number;
  /**
   * Vertical fudge applied to every field, in CSS px.
   *
   * react-pdf renders its page a little below the top of the host in a way
   * `pageOffset` doesn't capture, and this compensates. It is measured in screen
   * pixels rather than points, so it does not scale with zoom — callers whose
   * host IS the page box (the .docx overlay) must pass 0.
   */
  topNudgePx?: number;
  onFieldsChange: (fields: SignatureField[]) => void;
  selectedField: string | null;
  onSelectField: (fieldId: string | null) => void;
  onPageClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const PARTY_COLORS = {
  employer: 'hsl(var(--selise-blue))',
  employee: 'hsl(var(--poly-green))',
};

const SIGNATORY_COLORS = [
  PARTY_COLORS.employer,
  PARTY_COLORS.employee,
  'hsl(var(--mauveine))',
  'hsl(var(--destructive))',
];

const applyAlpha = (color: string, alpha: number) => {
  const clamped = Math.max(0, Math.min(alpha, 1));

  // Handle HSL strings (including CSS variables)
  if (color.startsWith('hsl(') || color.startsWith('hsla(')) {
    const normalized = color
      .replace(/^hsla?\(/, 'hsla(')
      .replace(/\)\s*$/, '')
      .replace(/\/\s*[\d.]+\s*$/, '');
    return `${normalized} / ${clamped})`;
  }

  // Fallback for hex colors (legacy)
  const normalized = color.replace('#', '');
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;
  const int = Number.parseInt(value, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;

  return `rgba(${r}, ${g}, ${b}, ${clamped})`;
};

const getMinimumDimensions = (type: SignatureField['type']) => {
  switch (type) {
    case 'signature':
      return { width: 140, height: 48 };
    case 'date':
      return { width: 110, height: 34 };
    default:
      return { width: 140, height: 34 };
  }
};

export function SignatureFieldOverlay({
  fields,
  signatories,
  currentPage,
  scale,
  pageRef,
  pageElement,
  pageWidth = 612,
  pageWidthPt = 612,
  pageHeightPt = 792,
  topNudgePx = -14,
  onFieldsChange,
  selectedField,
  onSelectField,
}: SignatureFieldOverlayProps) {
  // Calculate proportional dimensions. `pageWidthPt`/`pageHeightPt` are the actual PDF page
  // dimensions in points; defaults (612×792) keep US-Letter callers unchanged.
  const baseWidth = pageWidth;
  const baseHeight = (baseWidth / pageWidthPt) * pageHeightPt;
  // Get the actual page element - prefer pageElement prop, fallback to pageRef, then parent element
  const overlayRef = useRef<HTMLDivElement>(null);
  const getPageElement = (): HTMLDivElement | null => {
    return (
      pageElement ||
      pageRef?.current ||
      (overlayRef.current?.parentElement as HTMLDivElement) ||
      null
    );
  };
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeState, setResizeState] = useState<{
    fieldId: string;
    startX: number;
    startY: number;
    initialWidth: number;
    initialHeight: number;
  } | null>(null);
  const [hoveredField, setHoveredField] = useState<string | null>(null);

  // The overlay covers its container, but the rendered PDF (`.react-pdf__Page`)
  // typically sits a few px inside that container (header/border/padding above
  // the canvas). We measure that delta and apply it to the initial render so
  // saved fields line up with the PDF visually. The drag math is intentionally
  // left alone — once the user moves a field, the stored coord becomes
  // verbatim what they placed and is their responsibility.
  const [pageOffset, setPageOffset] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const container = pageElement || pageRef?.current || overlayRef.current?.parentElement;
    if (!container) return;

    const measure = () => {
      const pdfPage = container.querySelector('.react-pdf__Page') as HTMLElement | null;
      if (!pdfPage) return;
      const cRect = container.getBoundingClientRect();
      const pRect = pdfPage.getBoundingClientRect();
      setPageOffset({
        x: pRect.left - cRect.left,
        y: pRect.top - cRect.top,
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    const pdfPage = container.querySelector('.react-pdf__Page');
    if (pdfPage) observer.observe(pdfPage);

    return () => observer.disconnect();
  }, [currentPage, scale, pageElement, pageRef, pageWidth]);

  // Use refs to access latest values in global event handlers
  const fieldsRef = useRef(fields);
  const onFieldsChangeRef = useRef(onFieldsChange);
  const isDraggingRef = useRef(isDragging);
  const isResizingRef = useRef(isResizing);
  const selectedFieldRef = useRef(selectedField);
  const dragStartRef = useRef(dragStart);
  const resizeStateRef = useRef(resizeState);

  useEffect(() => {
    fieldsRef.current = fields;
    onFieldsChangeRef.current = onFieldsChange;
    isDraggingRef.current = isDragging;
    isResizingRef.current = isResizing;
    selectedFieldRef.current = selectedField;
    dragStartRef.current = dragStart;
    resizeStateRef.current = resizeState;
  }, [fields, onFieldsChange, isDragging, isResizing, selectedField, dragStart, resizeState]);

  const handleFieldPointerDown = (fieldId: string, event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    // Route subsequent move/up events to this element even if the finger
    // strays outside it. Without pointer capture, dragging is unreliable on
    // touch devices and lost when the cursor exits the overlay on desktop.
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(false);
    setResizeState(null);
    onSelectField(fieldId);
    setIsDragging(true);
    const field = fields.find((f) => f.id === fieldId);
    const element = getPageElement();
    if (field && element) {
      const rect = element.getBoundingClientRect();
      // Calculate offset from pointer position to field top-left corner
      // Convert PDF points to screen pixels using proportional scaling
      const fieldScreenX = (field.x / pageWidthPt) * baseWidth * scale;
      const fieldScreenY = (field.y / pageHeightPt) * baseHeight * scale;
      setDragStart({
        x: event.clientX - rect.left - fieldScreenX,
        y: event.clientY - rect.top - fieldScreenY,
      });
    }
  };

  const handleResizePointerDown = (fieldId: string, event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    onSelectField(fieldId);
    setIsDragging(false);
    setDragStart(null);
    setIsResizing(true);
    setResizeState({
      fieldId,
      startX: event.clientX,
      startY: event.clientY,
      initialWidth: field.width,
      initialHeight: field.height,
    });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = getPageElement();
    if (!element) return;

    if (isResizing && resizeState) {
      // Convert pixel delta to PDF points using proportional scaling
      const deltaXPixels = event.clientX - resizeState.startX;
      const deltaYPixels = event.clientY - resizeState.startY;
      // Account for proportional scaling: convert screen pixels to PDF points
      const deltaX = (deltaXPixels / scale) * (pageWidthPt / baseWidth);
      const deltaY = (deltaYPixels / scale) * (pageHeightPt / baseHeight);

      const field = fields.find((f) => f.id === resizeState.fieldId);
      if (!field) return;

      const minSize = getMinimumDimensions(field.type);

      onFieldsChange(
        fields.map((f) =>
          f.id === resizeState.fieldId
            ? {
                ...f,
                width: Math.max(minSize.width, resizeState.initialWidth + deltaX),
                height: Math.max(minSize.height, resizeState.initialHeight + deltaY),
              }
            : f
        )
      );
      return;
    }

    if (!isDragging || !selectedField || !dragStart) return;

    const rect = element.getBoundingClientRect();

    // Get mouse position relative to page container
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    // Convert screen pixels to PDF points using proportional scaling
    // First convert to base width/height coordinates, then to PDF points
    const x = ((mouseX - dragStart.x) / scale) * (pageWidthPt / baseWidth);
    const y = ((mouseY - dragStart.y) / scale) * (pageHeightPt / baseHeight);

    // Ensure fields stay within page bounds (proportional to baseWidth/baseHeight)
    const field = fields.find((f) => f.id === selectedField);
    if (!field) return;

    const maxX = pageWidthPt - field.width;
    const maxY = pageHeightPt - field.height;

    onFieldsChange(
      fields.map((f) =>
        f.id === selectedField
          ? {
              ...f,
              x: Math.max(0, Math.min(x, maxX)),
              y: Math.max(0, Math.min(y, maxY)),
            }
          : f
      )
    );
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    setDragStart(null);
    setIsResizing(false);
    setResizeState(null);
  };

  // Add global pointer event listeners for smooth dragging across the page.
  // Pointer events (vs. mouse events) cover both mouse and touch input, so
  // signature fields can be dragged with a finger on phones/tablets.
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleGlobalPointerMove = (event: PointerEvent) => {
      const element = pageElement || pageRef?.current;
      if (!element) return;

      const currentResizeState = resizeStateRef.current;
      const currentIsResizing = isResizingRef.current;
      const currentIsDragging = isDraggingRef.current;
      const currentSelectedField = selectedFieldRef.current;
      const currentDragStart = dragStartRef.current;

      if (currentIsResizing && currentResizeState) {
        const deltaXPixels = event.clientX - currentResizeState.startX;
        const deltaYPixels = event.clientY - currentResizeState.startY;
        // Account for proportional scaling: convert screen pixels to PDF points
        const currentBaseWidth = pageWidth;
        const currentBaseHeight = (currentBaseWidth / pageWidthPt) * pageHeightPt;
        const deltaX = (deltaXPixels / scale) * (pageWidthPt / currentBaseWidth);
        const deltaY = (deltaYPixels / scale) * (pageHeightPt / currentBaseHeight);

        const field = fieldsRef.current.find((f) => f.id === currentResizeState.fieldId);
        if (!field) return;

        const minSize = getMinimumDimensions(field.type);

        onFieldsChangeRef.current(
          fieldsRef.current.map((f) =>
            f.id === currentResizeState.fieldId
              ? {
                  ...f,
                  width: Math.max(minSize.width, currentResizeState.initialWidth + deltaX),
                  height: Math.max(minSize.height, currentResizeState.initialHeight + deltaY),
                }
              : f
          )
        );
        return;
      }

      if (currentIsDragging && currentSelectedField && currentDragStart) {
        const rect = element.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        // Convert screen pixels to PDF points using proportional scaling
        const currentBaseWidth = pageWidth;
        const currentBaseHeight = (currentBaseWidth / pageWidthPt) * pageHeightPt;
        const x = ((mouseX - currentDragStart.x) / scale) * (pageWidthPt / currentBaseWidth);
        const y = ((mouseY - currentDragStart.y) / scale) * (pageHeightPt / currentBaseHeight);

        const field = fieldsRef.current.find((f) => f.id === currentSelectedField);
        if (!field) return;

        const maxX = pageWidthPt - field.width;
        const maxY = pageHeightPt - field.height;

        onFieldsChangeRef.current(
          fieldsRef.current.map((f) =>
            f.id === currentSelectedField
              ? {
                  ...f,
                  x: Math.max(0, Math.min(x, maxX)),
                  y: Math.max(0, Math.min(y, maxY)),
                }
              : f
          )
        );
      }
    };

    const handleGlobalPointerUp = () => {
      handlePointerUp();
    };

    document.addEventListener('pointermove', handleGlobalPointerMove);
    document.addEventListener('pointerup', handleGlobalPointerUp);
    document.addEventListener('pointercancel', handleGlobalPointerUp);

    return () => {
      document.removeEventListener('pointermove', handleGlobalPointerMove);
      document.removeEventListener('pointerup', handleGlobalPointerUp);
      document.removeEventListener('pointercancel', handleGlobalPointerUp);
    };
  }, [isDragging, isResizing, scale, pageElement, pageRef, pageWidth, pageWidthPt, pageHeightPt]);

  const handleDeleteField = (fieldId: string) => {
    onFieldsChange(fields.filter((f) => f.id !== fieldId));
    if (selectedField === fieldId) {
      onSelectField(null);
    }
  };

  // Filter fields for current page
  const pageFields = fields.filter((f) => f.pageNumber === currentPage);

  // console.log('pageFields', pageFields);

  // Debug logging
  // if (fields.length > 0 && pageFields.length === 0) {
  //   console.log(
  //     `[SignatureFieldOverlay] Page ${currentPage}: Found ${fields.length} total fields, but none for this page. Field page numbers:`,
  //     fields.map((f) => f.pageNumber)
  //   );
  // }

  // Debug: Log field positions and container info for current page
  // useEffect(() => {
  //   const element = pageElement || pageRef?.current;
  //   if (pageFields.length > 0 && element) {
  //     const containerRect = element.getBoundingClientRect();
  //     const pdfPage = element.querySelector('.react-pdf__Page');
  //     const pageRect = pdfPage?.getBoundingClientRect();

  //     console.log(`[SignatureFieldOverlay] Page ${currentPage} positioning info:`, {
  //       scale,
  //       containerSize: {
  //         width: containerRect.width,
  //         height: containerRect.height,
  //         expectedWidth: 612 * scale,
  //         expectedHeight: 792 * scale,
  //       },
  //       pageElementSize: pageRect
  //         ? {
  //             width: pageRect.width,
  //             height: pageRect.height,
  //             left: pageRect.left - containerRect.left,
  //             top: pageRect.top - containerRect.top,
  //           }
  //         : 'not found',
  //       fields: pageFields.map((f) => ({
  //         id: f.id,
  //         type: f.type,
  //         pdfPosition: `(${f.x.toFixed(1)}, ${f.y.toFixed(1)})`,
  //         pdfSize: `${f.width.toFixed(1)}×${f.height.toFixed(1)}`,
  //         screenPosition: `(${(f.x * scale).toFixed(1)}px, ${(f.y * scale).toFixed(1)}px)`,
  //         screenSize: `${(f.width * scale).toFixed(1)}×${(f.height * scale).toFixed(1)}px`,
  //       })),
  //     });
  //   }
  // }, [pageFields, currentPage, scale, pageRef, pageElement]);

  if (pageFields.length === 0) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0"
      style={{
        zIndex: 10,
        pointerEvents: isDragging || isResizing ? 'auto' : 'none',
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={(e) => {
        // Only deselect if clicking empty space in the overlay (not a field)
        if (selectedField) {
          e.stopPropagation();
          onSelectField(null);
        }
      }}
    >
      {pageFields.map((field) => {
        const signatory = signatories[field.signatoryIndex];
        const isSelected = selectedField === field.id;
        const isHovered = hoveredField === field.id;
        const accentColor =
          signatory?.color || SIGNATORY_COLORS[field.signatoryIndex] || 'hsl(var(--selise-blue))';
        const nameLine = signatory?.name || 'Signatory';

        return (
          <div
            key={field.id}
            className={`absolute group transition-all pointer-events-auto ${
              isSelected ? 'z-50' : isHovered ? 'z-40' : 'z-30'
            }`}
            style={{
              left: `${pageOffset.x + (field.x / pageWidthPt) * baseWidth * scale}px`,
              top: `${pageOffset.y + (field.y / pageHeightPt) * baseHeight * scale + topNudgePx}px`,
              width: `${(field.width / pageWidthPt) * baseWidth * scale}px`,
              height: `${(field.height / pageHeightPt) * baseHeight * scale}px`,
              overflow: 'visible',
              // Tell the browser this element handles its own pointer gestures
              // — otherwise touch-dragging scrolls the page instead of moving
              // the field.
              touchAction: 'none',
            }}
            onPointerDown={(e) => handleFieldPointerDown(field.id, e)}
            onMouseEnter={() => setHoveredField(field.id)}
            onMouseLeave={() => setHoveredField(null)}
            onClick={(e) => {
              e.stopPropagation();
              onSelectField(field.id);
            }}
          >
            <div
              className={`absolute inset-0 rounded border-2 transition-all flex items-center justify-center ${
                isDragging ? 'cursor-grabbing' : 'cursor-move'
              }`}
              style={{
                borderColor: accentColor,
                borderStyle: isSelected ? 'solid' : 'dashed',
                borderWidth: isSelected ? '2px' : '2px',
                backgroundColor: applyAlpha(accentColor, isSelected ? 0.2 : isHovered ? 0.15 : 0.1),
                boxShadow: isSelected
                  ? `0 4px 12px ${applyAlpha(accentColor, 0.4)}, 0 0 0 2px ${applyAlpha(accentColor, 0.2)}`
                  : isHovered
                    ? `0 2px 8px ${applyAlpha(accentColor, 0.3)}`
                    : 'none',
              }}
            >
              <div className="flex flex-col items-center justify-center px-2 text-center leading-tight">
                <span
                  className="text-[10px] font-semibold tracking-wide uppercase"
                  style={{ color: accentColor }}
                >
                  {field.type === 'signature' ? 'Signature' : 'Date'}
                </span>
                <span className="text-[9px] text-globe-grey mt-0.5">{nameLine} </span>
              </div>

              {isSelected && (
                <div
                  className="absolute -top-2 -left-2 w-5 h-5 rounded-full border-2 border-white shadow-lg flex items-center justify-center"
                  style={{ backgroundColor: accentColor }}
                >
                  <Move className="w-3 h-3 text-white" />
                </div>
              )}
            </div>

            {isSelected && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteField(field.id);
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 text-white rounded-full flex items-center justify-center transition-colors shadow-lg border-2 border-white z-10"
                  style={{ backgroundColor: 'hsl(var(--destructive))' }}
                  title="Delete field"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
                <div
                  className="absolute -bottom-1 -right-1 w-6 h-6 bg-white border-2 rounded cursor-nwse-resize shadow-md z-10"
                  style={{ borderColor: accentColor, touchAction: 'none' }}
                  onPointerDown={(e) => handleResizePointerDown(field.id, e)}
                  title="Resize"
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
